import * as React from 'react';
import { toast } from 'react-toastify';
import { Action, Dispatch } from 'redux';

import { AppState } from 'src';

import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { SavedCreditCard } from '@models/Account';
import { PaymentTerms, PaymentType } from '@models/Payment';
import { accountService } from '@services/AccountService';
import { requestAction } from '@common/components/common.actions';
import { ContractOrderType } from '@models/Contract';
import { ServiceWorkOrderViewModel } from '@models/ServiceWorkOrder';
import { swoService } from '@services/ServiceWorkOrderService';
import { ContractState } from 'src/contract';
import { NotFoundError } from '@services/BaseService';
import { HostedCheckoutTransactionCreateModel } from '@models/HostedCheckoutTransactionViewModel';

import { AppDispatcher } from '../_models/common.models';
import { actions } from 'react-redux-form';
import FormNames from '../_constants/FormNames.enum';
import { paymentService } from '../_services/PaymentService';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum PaymentActionType {
  GetSavedCreditCardsForSwoFailure = 'GetSavedCreditCardsForSwoFailure',
  GetSavedCreditCardsForSwoStart = 'GetSavedCreditCardsForSwoStart',
  GetSavedCreditCardsForSwoSuccess = 'GetSavedCreditCardsForSwoSuccess',
  ResetPaymentState = 'ResetPaymentState',
  SetPaymentAmountInitial = 'SetPaymentAmountInitial',
  SetPaymentDate = 'SetPaymentDate',
  SetPaymentSwo = 'SetPaymentSwo',
  SetPaymentTerms = 'SetPaymentTerms',
  SetPaymentType = 'SetPaymentType',
  SetSavedCreditCard = 'SetSavedCreditCard '
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
interface PaymentAction extends Action {
  readonly type: PaymentActionType;
  readonly orderType?: ContractOrderType;
}

export interface SaveCreditCardsFailureAction extends PaymentAction {}
export interface SaveCreditCardsStartAction extends PaymentAction {}
export interface SaveCreditCardsSuccessAction extends PaymentAction {}

export interface SetPaymentAmountInitialAction extends PaymentAction {
  readonly paymentAmount: number | null;
  readonly totalPrice: number;
}

export interface SetPaymentDateAction extends PaymentAction {
  readonly contractEndDate?: Date;
  readonly paymentDate: Date;
}

export interface SetPaymentTermsAction extends PaymentAction {
  readonly paymentTerms: PaymentTerms;
  readonly totalPrice: number;
}

export interface SetPaymentTypeAction extends PaymentAction {
  readonly isBalance: boolean;
  readonly paymentType: PaymentType;
  readonly totalPrice: number;
}

export interface SetPaymentTypeInitialAction extends PaymentAction {
  readonly paymentType: PaymentType;
  readonly totalPrice: number;
}

export interface SetSavedCreditCardAction extends PaymentAction {
  readonly isBalance: boolean;
  readonly savedCreditCard?: SavedCreditCard;
}

export interface ResetPaymentStateAction extends PaymentAction {}

export function buildCheckoutRequest(paymentData: HostedCheckoutTransactionCreateModel) {
  return async (dispatch: AppDispatcher) => {
    return dispatch(
      requestAction(
        () => paymentService.create(paymentData),
        d => actions.change(FormNames.HiddenPayeezyTestForm, { ...d, Ecommerce_flag: d.ecommerce_flag }),
        {
          hasToast: false,
          supressErrors: false
        }
      )
    );
  };
}

export function setPaymentAmountInitial(
  paymentAmount: number | null,
  totalPrice: number,
  orderType: ContractOrderType
): SetPaymentAmountInitialAction {
  return {
    type: PaymentActionType.SetPaymentAmountInitial,
    paymentAmount: paymentAmount,
    totalPrice: totalPrice,
    orderType: orderType
  };
}

export function setPaymentDate(paymentDate: Date, contractEndDate?: Date): SetPaymentDateAction {
  return {
    type: PaymentActionType.SetPaymentDate,
    contractEndDate: contractEndDate,
    paymentDate: paymentDate
  };
}

export function setPaymentTerms(
  paymentTerms: PaymentTerms,
  totalPrice: number,
  orderType: ContractOrderType
): SetPaymentTermsAction {
  return {
    type: PaymentActionType.SetPaymentTerms,
    paymentTerms: paymentTerms,
    totalPrice: totalPrice,
    orderType: orderType
  };
}

export function setPaymentType(
  paymentType: PaymentType,
  totalPrice: number,
  isBalance: boolean,
  orderType: ContractOrderType
): SetPaymentTypeAction {
  return {
    type: PaymentActionType.SetPaymentType,
    isBalance: isBalance,
    paymentType: paymentType,
    totalPrice: totalPrice,
    orderType: orderType
  };
}

export function setSavedCreditCard(
  creditCard: SavedCreditCard | undefined,
  isBalance: boolean
): SetSavedCreditCardAction {
  return {
    type: PaymentActionType.SetSavedCreditCard,
    isBalance: isBalance,
    savedCreditCard: creditCard
  };
}

export function resetPaymentState(): ResetPaymentStateAction {
  return {
    type: PaymentActionType.ResetPaymentState
  };
}

export interface SetPaymentSwoAction extends PaymentAction {
  readonly type: PaymentActionType.SetPaymentSwo;
  readonly swoVM: ServiceWorkOrderViewModel;
}
export function setPaymentSwo(swo: ServiceWorkOrderViewModel): SetPaymentSwoAction {
  return {
    type: PaymentActionType.SetPaymentSwo,
    swoVM: swo
  };
}
export function getPaymentSwo(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    return dispatch(requestAction(() => swoService.getSwo(id), setPaymentSwo));
  };
}

/**** GetSavedCreditCardsForSwo ****/
export interface GetSavedCreditCardsForSwoStartAction extends PaymentAction {
  readonly type: PaymentActionType.GetSavedCreditCardsForSwoStart;
}
export function getSavedCreditCardsForSwoStart(): GetSavedCreditCardsForSwoStartAction {
  return {
    type: PaymentActionType.GetSavedCreditCardsForSwoStart
  };
}
export interface GetSavedCreditCardsForSwoSuccessAction extends PaymentAction {
  readonly type: PaymentActionType.GetSavedCreditCardsForSwoSuccess;
  readonly savedCreditCards: SavedCreditCard[];
}
export function getSavedCreditCardsForSwoSuccess(
  savedCreditCards: SavedCreditCard[]
): GetSavedCreditCardsForSwoSuccessAction {
  return {
    type: PaymentActionType.GetSavedCreditCardsForSwoSuccess,
    savedCreditCards: savedCreditCards
  };
}
export interface GetSavedCreditCardsForSwoFailureAction extends PaymentAction {
  readonly type: PaymentActionType.GetSavedCreditCardsForSwoFailure;
}
export function getSavedCreditCardsForSwoFailure(errorMessage: string): GetSavedCreditCardsForSwoFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);
  return {
    type: PaymentActionType.GetSavedCreditCardsForSwoFailure
  };
}
/**** GetSavedCreditCardsForSwo ****/
export function getSavedCreditCardsForSwo() {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(getSavedCreditCardsForSwoStart());

    try {
      // gather account ids
      // this is done as as collection because there might be more entities from which the credit card info can be pulled
      const contractState: ContractState = getStore().contract;
      const accountIds = {};
      if (contractState && contractState.contractDetailsVM) {
        const contract = contractState.contractDetailsVM;
        if (contract.buyer) {
          accountIds[contract.buyer.accountId!] = `Buyer: ${contract.buyer.name || ''}`;
        }
      }

      // gather stored credit cards
      const promises: Promise<SavedCreditCard | null>[] = [];
      for (const accountIdString of Object.keys(accountIds)) {
        const promise: Promise<SavedCreditCard | null> = accountService
          .getAccount(+accountIdString)
          .then(a => {
            return {
              account: a,
              name: accountIds[accountIdString]
            } as SavedCreditCard;
          })
          .catch(e => {
            if (e instanceof NotFoundError) {
              return null;
            }
            throw new Error(e.message);
          });
        promises.push(promise);
      }
      await Promise.all(promises);
      const savedCreditCards: SavedCreditCard[] = [];
      for (const promise of promises) {
        await promise.then(a => {
          if (a != null) {
            savedCreditCards.push(a);
          }
        });
      }

      return dispatch(getSavedCreditCardsForSwoSuccess(savedCreditCards));
    } catch (e) {
      return dispatch(getSavedCreditCardsForSwoFailure(e.message));
    }
  };
}



// WEBPACK FOOTER //
// ./src/payment/payment.actions.tsx